/**
 * Shared SSO Service for Demo Applications
 * Handles SSO token validation and authentication across subdomains
 */

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.syberkey.com';
const SSO_PORTAL_URL = process.env.REACT_APP_SSO_PORTAL_URL || 'https://sso.syberkey.com';

interface SSOUser {
  userId: string;
  email: string;
  fullName?: string;
}

interface SSOSession {
  sessionId?: string;
  expiresAt: string;
  issuedAt: string;
}

interface SSOValidationResponse {
  success: boolean;
  valid: boolean;
  user: SSOUser;
  session: SSOSession;
}

class SSOService {
  /**
   * Diagnostic function to check current state
   */
  diagnose(): void {
    console.log('[ssoService Accounts] ===== DIAGNOSTIC CHECK =====');
    console.log('[ssoService Accounts] Current URL:', window.location.href);
    console.log('[ssoService Accounts] Hostname:', window.location.hostname);
    console.log('[ssoService Accounts] Port:', window.location.port);
    
    // Check URL params
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('sso_token');
    console.log('[ssoService Accounts] Token in URL:', !!urlToken);
    
    // Check localStorage
    const ssoToken = localStorage.getItem('sso_token');
    const authToken = localStorage.getItem('auth_token');
    console.log('[ssoService Accounts] localStorage.sso_token:', ssoToken ? `${ssoToken.substring(0, 30)}... (length: ${ssoToken.length})` : 'null');
    console.log('[ssoService Accounts] localStorage.auth_token:', authToken ? `${authToken.substring(0, 30)}... (length: ${authToken.length})` : 'null');
    
    // Check cookies
    const cookies = document.cookie.split(';');
    console.log('[ssoService Accounts] All cookies:', cookies);
    const ssoCookie = cookies.find(c => c.trim().startsWith('sso_token='));
    console.log('[ssoService Accounts] sso_token cookie:', ssoCookie || 'not found');
    
    // Check logout flag
    const logoutFlag = localStorage.getItem('sso_logout_flag');
    console.log('[ssoService Accounts] Logout flag:', logoutFlag);
    
    console.log('[ssoService Accounts] ===== END DIAGNOSTIC =====');
  }

  /**
   * Validate if token is a valid JWT format
   */
  private isValidJWT(token: string): boolean {
    if (!token || typeof token !== 'string') {
      return false;
    }
    const parts = token.split('.');
    return parts.length === 3 && parts.every(part => part.length > 0);
  }

  /**
   * Get SSO token from URL parameter, cookie, or localStorage
   */
  private getSSOToken(): string | null {
    console.log('[ssoService Accounts] ===== GETTING SSO TOKEN =====');
    console.log('[ssoService Accounts] Current URL:', window.location.href);
    console.log('[ssoService Accounts] Hostname:', window.location.hostname);
    console.log('[ssoService Accounts] Port:', window.location.port);
    
    // First, check URL parameters (for localhost cross-port sharing)
    const urlParams = new URLSearchParams(window.location.search);
    const urlToken = urlParams.get('sso_token');
    console.log('[ssoService Accounts] Token in URL:', !!urlToken);
    if (urlToken) {
      console.log('[ssoService Accounts] URL token length:', urlToken.length);
      console.log('[ssoService Accounts] URL token preview:', urlToken.substring(0, 50));
    }
    
    if (urlToken) {
      console.log('[ssoService] Token from URL length:', urlToken.length);
      console.log('[ssoService] Token from URL preview:', urlToken.substring(0, 50));
      
      // Validate token format before storing
      if (!this.isValidJWT(urlToken)) {
        console.error('[ssoService] Invalid JWT token received from URL - not a valid JWT format!');
        console.error('[ssoService] Token preview:', urlToken.substring(0, 50));
        console.error('[ssoService] Token parts:', urlToken.split('.').length);
        // Clean URL without storing invalid token
        const newUrl = window.location.pathname + (window.location.hash || '');
        window.history.replaceState({}, document.title, newUrl);
        return null;
      }
      
      console.log('[ssoService Accounts] Valid JWT token found in URL, storing in localStorage...');
      // Store in localStorage for future use and remove from URL
      localStorage.setItem('sso_token', urlToken);
      localStorage.setItem('auth_token', urlToken);
      
      // Clear logout flag if it exists (new session is being established)
      if (localStorage.getItem('sso_logout_flag') === 'true') {
        console.log('[ssoService Accounts] Clearing logout flag - new session established');
        localStorage.removeItem('sso_logout_flag');
      }
      
      // Clean URL without reload
      const newUrl = window.location.pathname + (window.location.hash || '');
      window.history.replaceState({}, document.title, newUrl);
      console.log('[ssoService Accounts] Token stored, URL cleaned');
      return urlToken;
    }
    
    // For localhost, cookies don't work across ports, so use localStorage
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    console.log('[ssoService Accounts] Is localhost:', isLocalhost);
    
    if (isLocalhost) {
      // For localhost, use localStorage (shared via SSO portal setting it or URL parameter)
      console.log('[ssoService Accounts] Checking localStorage...');
      const ssoToken = localStorage.getItem('sso_token');
      const authToken = localStorage.getItem('auth_token');
      console.log('[ssoService Accounts] localStorage.sso_token exists:', !!ssoToken);
      console.log('[ssoService Accounts] localStorage.auth_token exists:', !!authToken);
      
      const token = ssoToken || authToken;
      console.log('[ssoService Accounts] Token in localStorage:', !!token);
      if (token) {
        console.log('[ssoService Accounts] Token from localStorage length:', token.length);
        console.log('[ssoService Accounts] Token from localStorage preview:', token.substring(0, 30));
        console.log('[ssoService Accounts] Token has dots:', token.includes('.'));
        console.log('[ssoService Accounts] Token parts:', token.split('.').length);
      } else {
        console.warn('[ssoService Accounts] NO TOKEN FOUND IN LOCALSTORAGE!');
        console.log('[ssoService Accounts] All localStorage keys:', Object.keys(localStorage));
      }
      
      if (token && !this.isValidJWT(token)) {
        console.error('[ssoService Accounts] Invalid JWT token in localStorage - clearing it');
        console.error('[ssoService Accounts] Token parts count:', token.split('.').length);
        localStorage.removeItem('sso_token');
        localStorage.removeItem('auth_token');
        return null;
      }
      
      if (token) {
        console.log('[ssoService Accounts] ✓ Valid token found in localStorage');
      } else {
        console.error('[ssoService Accounts] ✗ NO TOKEN FOUND - Will redirect to login');
      }
      
      return token;
    }
    
    // For production (subdomains), try cookie first
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'sso_token' && value) {
        if (!this.isValidJWT(value)) {
          console.error('[ssoService] Invalid JWT token in cookie - ignoring it');
          continue;
        }
        return value;
      }
    }
    
    // Fallback to localStorage
    const token = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
    if (token && !this.isValidJWT(token)) {
      console.error('[ssoService] Invalid JWT token in localStorage - clearing it');
      localStorage.removeItem('sso_token');
      localStorage.removeItem('auth_token');
      return null;
    }
    return token;
  }

  /**
   * Validate SSO token with backend
   */
  async validateToken(): Promise<SSOValidationResponse | null> {
    console.log('[ssoService Accounts] Validating token...');
    const token = this.getSSOToken();
    
    if (!token) {
      console.log('[ssoService Accounts] No token found for validation');
      return null;
    }

    console.log('[ssoService Accounts] Token found, length:', token.length);
    console.log('[ssoService Accounts] Calling validation API:', `${API_BASE_URL}/sso/validate-token`);
    console.log('[ssoService Accounts] API_BASE_URL:', API_BASE_URL);
    
    try {
      const response = await fetch(`${API_BASE_URL}/sso/validate-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      console.log('[ssoService Accounts] Validation response status:', response.status);
      console.log('[ssoService Accounts] Response ok:', response.ok);
      console.log('[ssoService Accounts] Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (response.ok) {
        const data: SSOValidationResponse = await response.json();
        console.log('[ssoService Accounts] ===== VALIDATION SUCCESS =====');
        console.log('[ssoService Accounts] Response data:', JSON.stringify(data, null, 2));
        console.log('[ssoService Accounts] data.valid:', data.valid);
        console.log('[ssoService Accounts] Session ID:', data.session?.sessionId);
        console.log('[ssoService Accounts] data.success:', (data as any).success);
        console.log('[ssoService Accounts] User from validation:', data.user);
        
        // Ensure valid is set correctly
        if ((data as any).success === true && data.valid === undefined) {
          console.log('[ssoService Accounts] Setting valid to true based on success flag');
          data.valid = true;
        }
        
        if (!data.valid) {
          console.error('[ssoService Accounts] ⚠️ Response says valid=false even though status is 200');
        }
        
        return data;
      } else {
        const errorText = await response.text();
        console.error('[ssoService Accounts] ===== VALIDATION FAILED =====');
        console.error('[ssoService Accounts] Token validation failed with status:', response.status);
        console.error('[ssoService Accounts] Error response:', errorText);
        
        // Check if it's a session invalidation (cross-app logout)
        try {
          const errorData = JSON.parse(errorText);
          if (errorData.error === 'session_invalidated' || errorData.crossAppLogout) {
            console.log('[ssoService Accounts] Session invalidated - cross-app logout detected');
            // Set logout flag to notify other components
            this.setLogoutFlag();
          }
        } catch (e) {
          // Not JSON, ignore
        }
        
        console.error('[ssoService Accounts] Clearing token due to validation failure');
        // Token is invalid or expired
        this.clearToken();
        return null;
      }
    } catch (error) {
      console.error('[ssoService Accounts] SSO token validation failed:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.error('[ssoService Accounts] Error details:', errorMessage);
      console.error('[ssoService Accounts] Error stack:', error instanceof Error ? error.stack : 'N/A');
      return null;
    }
  }

  /**
   * Check if user is authenticated
   */
  async isAuthenticated(): Promise<boolean> {
    console.log('[ssoService Accounts] ===== CHECKING AUTHENTICATION =====');
    const token = this.getSSOToken();
    console.log('[ssoService Accounts] Token found:', !!token);
    
    if (!token) {
      console.error('[ssoService Accounts] ✗ NO TOKEN FOUND - Authentication will fail');
      console.error('[ssoService Accounts] localStorage.sso_token:', localStorage.getItem('sso_token') ? 'exists' : 'null');
      console.error('[ssoService Accounts] localStorage.auth_token:', localStorage.getItem('auth_token') ? 'exists' : 'null');
      console.error('[ssoService Accounts] All localStorage:', Object.keys(localStorage).map(k => `${k}: ${localStorage.getItem(k)?.substring(0, 20)}...`));
      console.error('[ssoService Accounts] ===== NO TOKEN - RETURNING FALSE =====');
      return false;
    }
    
    console.log('[ssoService Accounts] Token found, length:', token.length);
    console.log('[ssoService Accounts] Token preview:', token.substring(0, 30));
    console.log('[ssoService Accounts] Calling validateToken()...');
    
    const validation = await this.validateToken();
    console.log('[ssoService Accounts] ===== VALIDATION RESULT =====');
    console.log('[ssoService Accounts] Validation result:', validation);
    console.log('[ssoService Accounts] validation !== null:', validation !== null);
    console.log('[ssoService Accounts] validation.valid:', validation?.valid);
    console.log('[ssoService Accounts] validation.success:', (validation as any)?.success);
    
    const isAuth = validation !== null && validation.valid === true;
    console.log('[ssoService Accounts] Final authentication result:', isAuth);
    
    if (!isAuth) {
      console.error('[ssoService Accounts] ✗ USER IS NOT AUTHENTICATED');
      console.error('[ssoService Accounts] Validation details:', JSON.stringify(validation, null, 2));
      console.error('[ssoService Accounts] ===== AUTHENTICATION FAILED =====');
    } else {
      console.log('[ssoService Accounts] ✓ USER IS AUTHENTICATED');
      console.log('[ssoService Accounts] User:', validation.user);
    }
    
    return isAuth;
  }

  /**
   * Get current user from SSO token
   */
  async getCurrentUser(): Promise<SSOUser | null> {
    const validation = await this.validateToken();
    return validation?.user || null;
  }

  /**
   * Redirect to SSO portal for login
   */
  redirectToSSO(returnUrl?: string): void {
    const currentUrl = returnUrl || window.location.href;
    const redirectUrl = `${SSO_PORTAL_URL}/login?redirect=${encodeURIComponent(currentUrl)}`;
    
    console.log('[ssoService Accounts] ===== REDIRECT BREAKPOINT =====');
    console.log('[ssoService Accounts] About to redirect to:', redirectUrl);
    console.log('[ssoService Accounts] Current URL:', currentUrl);
    console.log('[ssoService Accounts] SSO_PORTAL_URL:', SSO_PORTAL_URL);
    console.log('[ssoService Accounts] =================================');
    
    // DEBUG: Check if debug mode is enabled (via URL param or localStorage)
    const urlParams = new URLSearchParams(window.location.search);
    const debugMode = urlParams.get('debug') === 'true' || localStorage.getItem('sso_debug_mode') === 'true';
    
    if (debugMode) {
      console.warn('[ssoService Accounts] DEBUG MODE: Redirect disabled. Set ?debug=false or localStorage.removeItem("sso_debug_mode") to enable redirect.');
      alert(`DEBUG MODE: Would redirect to ${redirectUrl}\n\nCheck console for logs.`);
      return;
    }
    
    // DEBUG: Add breakpoint - will pause execution if DevTools is open
    debugger;
    
    // DEBUG: Add delay to allow logs to be seen
    console.log('[ssoService Accounts] Waiting 3 seconds before redirect (to see logs)...');
    setTimeout(() => {
      console.log('[ssoService Accounts] Redirecting now...');
      window.location.href = redirectUrl;
    }, 3000);
  }

  /**
   * Clear SSO token
   */
  clearToken(): void {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    
    if (!isLocalhost) {
      // Clear cookie for production
      document.cookie = 'sso_token=; domain=.syberkey.com; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=None; Secure';
    }
    
    // Clear localStorage
    localStorage.removeItem('sso_token');
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  }

  /**
   * Check if logout was triggered from another tab/app
   * Only returns true if logout flag exists AND there's no valid token
   */
  checkLogoutFlag(): boolean {
    const logoutFlag = localStorage.getItem('sso_logout_flag');
    if (logoutFlag === 'true') {
      // Check if we have a valid token in URL or storage - if yes, clear the flag (false alarm)
      const urlParams = new URLSearchParams(window.location.search);
      const urlToken = urlParams.get('sso_token');
      const storageToken = localStorage.getItem('sso_token') || localStorage.getItem('auth_token');
      
      // If we have a token (URL or storage), this is a false alarm - clear the flag
      if (urlToken || storageToken) {
        const token = urlToken || storageToken;
        if (token && this.isValidJWT(token)) {
          console.log('[ssoService Accounts] Logout flag found but valid token exists - clearing flag (false alarm)');
          localStorage.removeItem('sso_logout_flag');
          return false;
        }
      }
      
      console.log('[ssoService Accounts] Logout flag detected - session invalidated from another app');
      return true;
    }
    return false;
  }

  /**
   * Set logout flag to notify other tabs/apps
   */
  setLogoutFlag(): void {
    localStorage.setItem('sso_logout_flag', 'true');
    // Remove flag after a short delay to allow other tabs to detect it
    setTimeout(() => {
      localStorage.removeItem('sso_logout_flag');
    }, 1000);
  }

  /**
   * Logout - Google-style cross-application logout
   * Invalidates all sessions for the user across all applications
   */
  async logout(): Promise<void> {
    console.log('[ssoService Accounts] Logout initiated - Google-style cross-app logout');
    const token = this.getSSOToken();
    
    if (token) {
      try {
        console.log('[ssoService Accounts] Calling logout API to invalidate all sessions...');
        const response = await fetch(`${API_BASE_URL}/sso/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token,
            allSessions: true // Always true for Google-style cross-app logout
          })
        });

        if (response.ok) {
          const result = await response.json();
          console.log('[ssoService Accounts] Logout API call successful');
          console.log('[ssoService Accounts] Sessions invalidated:', result.sessionsInvalidated);
          console.log('[ssoService Accounts] Cross-app logout:', result.crossAppLogout);
        } else {
          const errorText = await response.text();
          console.error('[ssoService Accounts] Logout API call failed with status:', response.status);
          console.error('[ssoService Accounts] Error response:', errorText);
        }
      } catch (error) {
        console.error('[ssoService Accounts] Logout API call failed:', error);
        // Continue with local logout even if API call fails
      }
    }

    // Set logout flag to notify other tabs/apps immediately
    this.setLogoutFlag();
    
    // Clear tokens locally
    this.clearToken();
    
    // Broadcast logout event to other tabs/windows
    window.localStorage.setItem('sso_logout_event', Date.now().toString());
    window.localStorage.removeItem('sso_logout_event');
    
    console.log('[ssoService Accounts] Redirecting to SSO portal...');
    window.location.href = `${SSO_PORTAL_URL}/`;
  }

  /**
   * Check session status (lightweight check for periodic validation)
   */
  async checkSessionStatus(): Promise<boolean> {
    const token = this.getSSOToken();
    if (!token) {
      return false;
    }

    try {
      // Decode token to get sessionId (if available)
      const parts = token.split('.');
      if (parts.length === 3) {
        try {
          const payload = JSON.parse(atob(parts[1]));
          const sessionId = payload.sessionId;
          
          if (sessionId) {
            // Lightweight session check
            const response = await fetch(`${API_BASE_URL}/sso/session/check?sessionId=${encodeURIComponent(sessionId)}`);
            if (response.ok) {
              const data = await response.json();
              return data.valid === true;
            }
          }
        } catch (e) {
          // If we can't decode token, fall back to full validation
        }
      }
      
      // Fallback to full token validation
      const validation = await this.validateToken();
      return validation !== null && validation.valid === true;
    } catch (error) {
      console.error('[ssoService Accounts] Session status check failed:', error);
      return false;
    }
  }
}

export const ssoService = new SSOService();
export type { SSOUser, SSOSession, SSOValidationResponse };
